import { useState, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimation,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ApiContext } from '../../App';
import ContainerBizDev from "./ContainerBizDev";
import { ProspectItem } from "./SortableProspectItem";
import BulkActionsPanel from "./BulkActionsPanel";
import ProcessBizDevListView from "./ProcessBizDevListView";
import { getAllEmails } from "../../utils/emailHelpers";

// Définition des styles
const wrapperStyle = {
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  paddingBottom: "16px",
  background: "#f5f5f5",
  overflowX: "auto",
  minHeight: "calc(100vh - 200px)",
  borderRadius: "0.5rem"
};

// Configuration de l'animation de drop
const dropAnimation = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.5,
};

export default function ProcessBizDevBoard({ processId, viewMode = 'kanban' }) {
  const API = useContext(ApiContext);
  const navigate = useNavigate();
  const [openIndividualModal, setOpenIndividualModal] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState(null);
  const [prospectsByStage, setProspectsByStage] = useState({});
  const [processSteps, setProcessSteps] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [processData, setProcessData] = useState(null);

  // État pour les filtres par colonne
  const [columnFilters, setColumnFilters] = useState({});

  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fonction de rafraîchissement des données
  const fetchProcessData = async () => {
    try {
      const response = await API.get(`/api/process-bizdev/${processId}`);
      const processData = response.data;

      // Récupérer les étapes actives
      const activeSteps = processData.steps
        .filter(step => step.active)
        .sort((a, b) => a.order - b.order);

      setProcessSteps(activeSteps);

      // Groupement des prospects par étape (en utilisant l'ID si disponible, sinon le nom)
      const grouped = processData.prospects.reduce((acc, prospect) => {
        let stageId;

        // Priorité à currentStepId si disponible
        if (prospect.currentStepId) {
          const step = activeSteps.find(s => s._id === prospect.currentStepId);
          stageId = step ? step._id : null;
        }

        // Fallback sur currentStep (nom) pour compatibilité
        if (!stageId) {
          const step = activeSteps.find(s => s.name === prospect.currentStep);
          stageId = step ? step._id : activeSteps[0]?._id;
        }

        if (stageId) {
          if (!acc[stageId]) acc[stageId] = [];
          acc[stageId].push(prospect);
        }

        return acc;
      }, {});

      // S'assurer que toutes les étapes actives existent, même vides
      const completeGrouped = activeSteps.reduce((acc, step) => {
        acc[step._id] = grouped[step._id] || [];
        return acc;
      }, {});

      setProspectsByStage(completeGrouped);
      setProcessData(processData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching process data:", err);
      setError("Impossible de charger les données du processus.");
      setLoading(false);
    }
  };

  // Chargement initial des données
  useEffect(() => {
    fetchProcessData();
  }, [processId]);

  const updateProspectStage = async (prospectId, newStageId) => {
    try {
      // Récupérer d'abord le prospect pour avoir son individualId
      const prospect = Object.values(prospectsByStage)
        .flat()
        .find(p => p._id === prospectId);

      if (!prospect) {
        console.error("Prospect not found:", prospectId);
        setError("Prospect non trouvé");
        return;
      }

      // Trouver l'étape correspondante pour envoyer son ID
      const targetStep = processSteps.find(s => s._id === newStageId);
      if (!targetStep) {
        console.error("Step not found:", newStageId);
        setError("Étape non trouvée");
        return;
      }

      const response = await API.put(
        `/api/process-bizdev/${processId}/prospects/${prospect.individual._id}/move`,
        { step: targetStep._id } // Envoyer l'ID de l'étape
      );

      console.log('Update successful:', response.data);
    } catch (err) {
      console.error("Error updating prospect stage:", err);
      setError("Une erreur s'est produite lors de la mise à jour du prospect.");
    }
  };

  // Trouver le conteneur d'un élément par son ID
  const findContainer = (id) => {
    if (id in prospectsByStage) return id;
    
    return Object.keys(prospectsByStage).find(key => 
      prospectsByStage[key].some(item => item._id === id)
    );
  };

  // Gestionnaire de début de drag
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
  };

  // Gestionnaire de fin de drag
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeContainer = findContainer(active.id);
    let overContainer = findContainer(over.id);
    
    // Si over.id est directement un nom de conteneur (drop sur une zone vide)
    if (!overContainer && over.id in prospectsByStage) {
      overContainer = over.id;
    }
    
    // Si on ne trouve toujours pas le conteneur, essayer via les données du drop
    if (!overContainer && over.data?.current?.type === 'container') {
      overContainer = over.data.current.droppableId;
    }
    
    if (!activeContainer || !overContainer) {
      setActiveId(null);
      return;
    }

    const activeIndex = prospectsByStage[activeContainer].findIndex(
      item => item._id === active.id
    );
    
    if (activeContainer === overContainer) {
      // Réorganisation dans le même conteneur
      const overIndex = prospectsByStage[overContainer].findIndex(
        item => item._id === over.id
      );
      
      if (activeIndex !== overIndex && overIndex !== -1) {
        setProspectsByStage(prev => ({
          ...prev,
          [overContainer]: arrayMove(
            prev[overContainer],
            activeIndex,
            overIndex
          ),
        }));
      }
    } else {
      // Déplacement vers un autre conteneur
      setProspectsByStage(prev => {
        const prospect = prev[activeContainer][activeIndex];
        
        return {
          ...prev,
          [activeContainer]: [
            ...prev[activeContainer].filter(item => item._id !== active.id),
          ],
          [overContainer]: [
            ...prev[overContainer],
            { ...prospect, currentStepId: overContainer },
          ],
        };
      });

      // Mise à jour dans la base de données
      updateProspectStage(active.id, overContainer);
    }

    setActiveId(null);
  };


  // Gestionnaire d'annulation de drag
  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Fonctions de gestion de la sélection
  const handleSelectProspect = (prospectId) => {
    setSelectedProspects(prev => 
      prev.includes(prospectId) 
        ? prev.filter(id => id !== prospectId)
        : [...prev, prospectId]
    );
  };

  const handleSelectColumn = (columnId, prospectIds, selectAll) => {
    setSelectedProspects(prev => {
      if (selectAll) {
        // Ajouter tous les prospects de cette colonne
        const newIds = prospectIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      } else {
        // Retirer tous les prospects de cette colonne
        return prev.filter(id => !prospectIds.includes(id));
      }
    });
  };

const handleNavigateToIndividual = (individualId) => {
  try {
    navigate(`/process/bizdev/${processId}/candidates/${individualId}`)
  } catch (error) {
    console.error("Error fetching individual details:", error);
    setError("Impossible de charger les détails de l'individual");
    setOpenIndividualModal(false);
  }
};


  const handleClearSelection = () => {
    setSelectedProspects([]);
  };

  // Gestionnaire pour les filtres de colonne
  const handleColumnFilterChange = (columnId, filters) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: filters
    }));
  };

  // Fonction pour filtrer les prospects d'une colonne
  const getFilteredProspects = (columnId, prospects) => {
    const filters = columnFilters[columnId];

    if (!filters || (!filters.hasProEmail && !filters.hasPersoEmail && !filters.hasPhone)) {
      return prospects;
    }

    return prospects.filter(prospect => {
      const individual = prospect.individual;
      if (!individual) return false;

      const checks = [];

      if (filters.hasProEmail) {
        const hasProfessionalEmail = getAllEmails(individual.professionalEmail).length > 0;
        checks.push(hasProfessionalEmail);
      }

      if (filters.hasPersoEmail) {
        const hasPersonalEmail = getAllEmails(individual.personalEmail).length > 0;
        checks.push(hasPersonalEmail);
      }

      if (filters.hasPhone) {
        const phoneString = individual.phoneNumber?.trim().toLowerCase();
        const hasValidPhone = phoneString &&
          phoneString !== '-' &&
          phoneString !== 'téléphone indisponible' &&
          phoneString !== 'null' &&
          phoneString !== 'telephone indisponible';
        checks.push(hasValidPhone);
      }

      // Si aucun filtre n'est actif, afficher tous les prospects
      if (checks.length === 0) return true;

      // Si au moins un filtre est actif, le prospect doit satisfaire TOUS les filtres actifs
      return checks.every(check => check === true);
    });
  };

  // Affichage du chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div>Chargement des prospects...</div>
      </div>
    );
  }

  // Affichage des erreurs
  if (error) {
    return (
      <div className="text-red-500 p-4 text-center">
        {error}
      </div>
    );
  }

  // Rendu principal
  return (
    <>
      {selectedProspects.length > 0 && (
        <BulkActionsPanel 
          selectedProspects={selectedProspects}
          processData={processData}
          onClearSelection={handleClearSelection}
          onRefresh={() => window.location.reload()} // Simple refresh pour maintenant
        />
      )}

      {viewMode === 'kanban' ? (
        <div style={wrapperStyle}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            {processSteps.map((step) => {
              const allProspects = prospectsByStage[step._id] || [];
              const filteredProspects = getFilteredProspects(step._id, allProspects);

              return (
                <ContainerBizDev
                  key={step._id}
                  id={step._id}
                  title={step.name}
                  items={filteredProspects}
                  allItems={allProspects}
                  selectedProspects={selectedProspects}
                  onSelectProspect={handleSelectProspect}
                  onSelectColumn={handleSelectColumn}
                  onNavigateToIndividual={handleNavigateToIndividual}
                  filters={columnFilters[step._id]}
                  onFilterChange={(filters) => handleColumnFilterChange(step._id, filters)}
                />
              );
            })}

            <DragOverlay dropAnimation={dropAnimation}>
              {activeId ? (
                <ProspectItem
                  prospect={
                    Object.values(prospectsByStage)
                      .flat()
                      .find((prospect) => prospect._id === activeId)
                  }
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      ) : (
        <ProcessBizDevListView
          prospectsByStage={prospectsByStage}
          processSteps={processSteps}
          selectedProspects={selectedProspects}
          onSelectProspect={handleSelectProspect}
          onNavigateToIndividual={handleNavigateToIndividual}
          onMoveToNextStage={updateProspectStage}
          onRefresh={fetchProcessData}
        />
      )}
      </>
  );


}